import { VideoResolution } from '@/ai/video-models';
import { getTrendCredits, TRENDS } from '@/config/trends';
import { getLocaleCopy, type Locale } from '@/lib/locale';
import { Routes } from '@/lib/routes';

export interface HomeTextLink {
  label: string;
  href: string;
}

export interface HomeCopy {
  seoTitle: string;
  seoDescription: string;
  keywords: string[];
  title: string;
  /** Trailing words of `title` shown in the green accent colour. */
  titleAccent: string;
  subtitle: string;
  hero: {
    eyebrowLead: string;
    eyebrowAccent: string;
    chips: string[];
    ctaLabel: string;
    illustrationLabel: string;
    disclaimer: string;
  };
  quickAnswer: string;
  quickFacts: { label: string; value: string }[];
  comparison: {
    title: string;
    intro: string;
    columns: [string, string, string, string];
    rows: [string, string, string, string][];
  };
  pain: { title: string; paragraphs: string[] };
  features: {
    title: string;
    intro: string;
    items: { title: string; body: string }[];
  };
  advantage: {
    title: string;
    items: { title: string; body: string }[];
  };
  input: { title: string; intro: string; items: string[] };
  steps: {
    title: string;
    intro: string;
    items: { name: string; text: string }[];
  };
  useCases: {
    title: string;
    columns: [string, string, string, string];
    rows: [string, string, string, string][];
  };
  decision: {
    title: string;
    columns: [string, string, string];
    rows: { goal: string; tool: HomeTextLink | string; why: string }[];
  };
  fileHandling: { title: string; items: string[]; pricingLink: HomeTextLink };
  pricing: { title: string; intro: string };
  faqTitle: string;
  faqs: { question: string; answer: string }[];
  cta: { title: string; body: string; button: string };
  disclaimer: string;
  lastUpdatedLabel: string;
}

const TREND = TRENDS['crazy-frog'];
const SECONDS = TREND.templateSeconds;
const SD_CREDITS = getTrendCredits(TREND, VideoResolution.P480);
const HD_CREDITS = getTrendCredits(TREND, VideoResolution.P720);
const SCENE_COUNT = TREND.scenes?.items.length ?? 0;

/**
 * Homepage copy for crazyfrog-ai.online: one page for the "crazy frog ai"
 * invisible-motorbike chase trend, built around the trend workbench, which
 * re-films the original trend clip with the visitor's photo.
 * Only English copy exists for now; the Spanish homepage falls back to it and
 * stays `noindex` until a translation is written (see `src/lib/locale.ts`).
 */
const HOME_COPY: { en: HomeCopy } = {
  en: {
    seoTitle: 'Crazy Frog AI Video Generator: Invisible Motorbike Chase',
    seoDescription:
      'Make the crazy frog ai video with your own face: upload two photos, pick a background, and get the invisible motorbike chase, shot for shot.',
    keywords: [
      'crazy frog ai',
      'crazy frog ai video generator',
      'crazy frog ai trend',
      'invisible motorbike ai video',
      'crazy frog ai template',
    ],
    title: 'Crazy Frog AI Video Generator',
    titleAccent: 'Video Generator',
    subtitle:
      'Two photos in: the rider and the chaser. The original invisible motorbike chase out.',
    hero: {
      eyebrowLead: 'Ride the',
      eyebrowAccent: 'invisible motorbike',
      chips: [
        '2 photos',
        `${SCENE_COUNT + 1} backgrounds`,
        `${SECONDS}s, shot for shot`,
      ],
      ctaLabel: 'Upload your photos',
      illustrationLabel:
        'A rider on an invisible motorbike, drawn as a dashed neon outline, is chased down a highway',
      disclaimer:
        'Independent fan-made tool. Not affiliated with the Crazy Frog brand.',
    },
    quickAnswer: `This crazy frog ai video generator re-films the viral invisible motorbike chase with two people from your photos: one rides the invisible motorbike in front, the other chases on the red hover-bike. The motion, camera, and cuts come from the original trend clip, so only the two riders and, if you want, the place change. It does not animate the cartoon Crazy Frog character.`,
    quickFacts: [
      {
        label: 'Input',
        value:
          'Two photos, one person each: the foreground rider and the chaser (JPEG, PNG, or WebP, up to 10MB), plus a background.',
      },
      {
        label: 'Output',
        value: `A ${SECONDS}-second MP4 that follows the original clip, at 480p or 720p.`,
      },
      {
        label: 'Cost',
        value: `${SD_CREDITS} credits at 480p, ${HD_CREDITS} credits at 720p. Shown on the Generate button.`,
      },
      {
        label: 'Failed runs',
        value:
          'Credits for a failed or blocked generation return automatically.',
      },
    ],
    comparison: {
      title: 'How is a crazy frog ai clip different from a CapCut template?',
      intro:
        'A crazy frog ai clip made here is new AI footage of the two people in your photos, filmed along the original chase. An editing-app template only puts effects over video you already filmed.',
      columns: ['Route', 'You start with', 'You get', 'Best when'],
      rows: [
        [
          'This crazy frog ai generator',
          'Two photos and a background',
          'The original chase, shot for shot, with you on the invisible motorbike',
          'You want the real trend with your face',
        ],
        [
          'Editing-app template',
          'A clip you filmed yourself',
          'Trend effects and sound placed over your own footage',
          'You already filmed yourself in the squat',
        ],
        [
          'Prompt-only AI video',
          'A long text prompt and a photo',
          'A new chase invented from the prompt, different every run',
          'You want your own take on the idea',
        ],
      ],
    },
    pain: {
      title: 'Why is the invisible motorbike chase hard to get right?',
      paragraphs: [
        'The joke only works when nothing is under the rider. General video models like to add a real scooter, turn the squat into running, or drift away from the face halfway through.',
        'Writing a prompt that recreates the same cuts and camera moves as the trend clip almost never works: a prompt invents new motion every time.',
        'This crazy frog ai generator skips the prompt. The original clip sets the motion, and your two photos only set who rides and who chases.',
      ],
    },
    features: {
      title: 'Why make your crazy frog ai video here?',
      intro:
        'Four things are set up for the invisible motorbike chase before you upload anything.',
      items: [
        {
          title: 'The original chase, shot for shot',
          body: 'The motion, camera path, and cuts come from the trend clip itself, so your video lines up with the one everyone is posting.',
        },
        {
          title: 'You ride, your friend chases',
          body: 'Upload one photo for the rider in front and one for the chaser behind. The AI recasts both with their own face, hair, and clothes for the whole clip.',
        },
        {
          title: `${SCENE_COUNT + 1} backgrounds`,
          body: `Keep the original road, or move the chase to a neon city, a desert canyon, Mars, or ${SCENE_COUNT - 3} other places. You can also upload a photo of your own place.`,
        },
        {
          title: 'Clear credits and automatic refunds',
          body: 'The Generate button shows the credit cost before you confirm. If a generation fails or is blocked, its credits go back to your balance.',
        },
      ],
    },
    advantage: {
      title: 'Why does the crazy frog ai rider still look like you?',
      items: [
        {
          title: 'One person per photo, one role each',
          body: 'Each upload is locked to one role, so the rider and the chaser never swap, and the model spends its effort on the faces instead of inventing a whole scene.',
        },
        {
          title: 'Motion copied, not guessed',
          body: 'Because the chase comes from the real clip, the squat, the fists on handlebars that are not there, and the empty air under the rider stay intact.',
        },
      ],
    },
    input: {
      title: 'What photos work best for a crazy frog ai video?',
      intro:
        'Two clear photos are all the crazy frog ai workbench needs: the rider in front and the chaser behind. The chase comes from the original clip.',
      items: [
        'One person per photo, face clearly visible and turned toward the camera.',
        'Waist-up or full body, so the clothes carry over to the ride.',
        'Sharp and evenly lit. Sunglasses, heavy filters, and side profiles make the face less accurate.',
        'JPEG, PNG, or WebP, up to 10MB.',
        'Only photos of yourself or of people who agreed to appear. No celebrities or strangers.',
      ],
    },
    steps: {
      title: 'How do I make a crazy frog ai video from two photos?',
      intro: 'Five steps take you from two photos to a clip you can post.',
      items: [
        {
          name: 'Upload two photos',
          text: 'Add a clear photo of the foreground lead, who rides the invisible motorbike, and one of the chaser on the red hover-bike behind.',
        },
        {
          name: 'Pick a background',
          text: `Keep the original road, choose one of ${SCENE_COUNT} places, or upload your own.`,
        },
        {
          name: 'Pick a resolution',
          text: `480p costs ${SD_CREDITS} credits and 720p costs ${HD_CREDITS}. The length always matches the original clip.`,
        },
        {
          name: 'Generate and wait',
          text: 'Press Generate. Most videos are ready in 5 to 10 minutes, and they are saved to your history.',
        },
        {
          name: 'Post it',
          text: 'Download the MP4, add the trend sound in the app where you post, and label it as AI-generated.',
        },
      ],
    },
    useCases: {
      title: 'Who uses the invisible motorbike chase, and how?',
      columns: ['Scenario', 'Starting photo', 'Background', 'Result'],
      rows: [
        [
          'Put yourself in the trend',
          'Your selfie and a friend’s',
          'Original',
          'The trend clip, shot for shot, with you riding and your friend chasing',
        ],
        [
          'Chase your favorite person',
          'Two people who said yes',
          'Any preset',
          'One escapes, one chases, through the place you picked',
        ],
        [
          'Your own twist',
          'You and a coworker or classmate',
          'A photo of your street, office, or school',
          'The same chase, filmed in a place your followers know',
        ],
      ],
    },
    decision: {
      title: 'Which tool should I use for each version of the chase?',
      columns: ['Your goal', 'Use', 'Why'],
      rows: [
        {
          goal: 'The original chase with my face',
          tool: 'This crazy frog ai workbench',
          why: 'It follows the trend clip shot for shot.',
        },
        {
          goal: 'A brand-new chase from several stills',
          tool: { label: 'Reference to Video', href: Routes.ReferenceToVideo },
          why: 'It takes several stills and a prompt, and invents new motion.',
        },
        {
          goal: 'No photo, an invented rider',
          tool: { label: 'Text to Video', href: Routes.TextToVideo },
          why: 'Describe the rider and the chase in words only.',
        },
        {
          goal: 'Make a finished clip longer',
          tool: { label: 'AI Video Extender', href: Routes.VideoExtend },
          why: 'It continues a completed clip with a few more seconds.',
        },
      ],
    },
    fileHandling: {
      title: 'What happens to my photos and the finished clip?',
      items: [
        'You sign in to generate, so every crazy frog ai clip lands in your own account.',
        'Your photos and background are only used for the video you ask for.',
        'Finished clips are saved to your history, where you can watch and download them again.',
        'Generating needs credits from a one-time credit pack. There is no subscription.',
      ],
      pricingLink: {
        label: 'See credit packs',
        href: Routes.Pricing,
      },
    },
    pricing: {
      title: 'Credit packs',
      intro: `Buy credits once. They never expire, and a failed generation is refunded. One ${SECONDS}-second crazy frog ai video is ${SD_CREDITS} credits at 480p.`,
    },
    faqTitle: 'Questions about the invisible motorbike clip',
    faqs: [
      {
        question: 'Is this the official Crazy Frog app?',
        answer:
          'No. This is an independent, fan-made crazy frog ai video generator, and it is not affiliated with or endorsed by the owners of the Crazy Frog brand. Your clip shows the people in your photos, not the cartoon frog, and it uses no official logo, artwork, or music.',
      },
      {
        question: 'Will it match the original TikTok?',
        answer:
          'Yes, shot for shot. The AI follows the motion, camera path, and cuts of the original trend clip and only changes the two riders and, if you choose, the place. Faces and small details still vary a little from run to run.',
      },
      {
        question: 'Can I change the background?',
        answer: `Yes. Keep the original road, pick one of ${SCENE_COUNT} places (neon city, desert canyon, snowy pass, beach road, supermarket, Mars highway, or candy village), or upload a photo of your own place. Your picture sets the colors, materials, and light, while the road and camera path keep the original chase shape. Wide, open places work best.`,
      },
      {
        question: 'How much does a crazy frog ai video cost?',
        answer: `${SD_CREDITS} credits at 480p or ${HD_CREDITS} credits at 720p. Credits come from one-time packs, starting with 50 credits for $9.90, and they never expire. There are no free credits; if a video fails, its credits are refunded automatically.`,
      },
      {
        question: 'Does the clip include the Axel F song?',
        answer:
          'No. This site does not add the Axel F song or any other licensed music to a crazy frog ai clip. Add the trend sound inside TikTok, Instagram, or YouTube when you post, and follow that platform’s music rules.',
      },
      {
        question: 'How long does it take?',
        answer:
          'Most videos are ready in 5 to 10 minutes. You can leave the page: the video keeps rendering and appears in your history when it is done.',
      },
    ],
    cta: {
      title: 'Ready to ride the invisible motorbike?',
      body: 'Upload two photos and the workbench above films you and a friend into the original chase.',
      button: 'Make my video',
    },
    disclaimer:
      'Crazy Frog is a registered trademark of its owner. This crazy frog ai site is an independent, fan-made tool. It is not affiliated with, endorsed by, or sponsored by the Crazy Frog brand or its owners, and it uses no official Crazy Frog artwork, logos, characters, or music.',
    lastUpdatedLabel: 'Last updated',
  },
};

export function getHomeCopy(locale?: Locale): HomeCopy {
  return getLocaleCopy(HOME_COPY, locale);
}
